import { City, Country, State } from "country-state-city";

import COUNTRIES from "./Countries";
import PROVINCES from "./Provinces";
import CITIES from "./Cities";

const PHILIPPINES = "Philippines";
// Philippines uses the local PRPCM province/city lists by default because the
// country-state-city dataset has incomplete PH city coverage (most provinces
// return no cities). Set this to false to source PH options from the library too.
const USE_LEGACY_PHILIPPINE_LOCATIONS = true;

const normalizeName = (value) => (value || "").trim().toLowerCase();

export const isPhilippines = (countryName) => countryName === PHILIPPINES;

export const getCountryOptions = () => COUNTRIES;

export const findCountryByName = (countryName) => {
  const normalizedCountry = normalizeName(countryName);
  const localCountry = COUNTRIES.find(
    ({ name }) => normalizeName(name) === normalizedCountry
  );

  return localCountry?.code
    ? Country.getCountryByCode(localCountry.code)
    : undefined;
};

export const getRegionOptions = (countryName) => {
  if (!countryName) {
    return [];
  }

  if (isPhilippines(countryName) && USE_LEGACY_PHILIPPINE_LOCATIONS) {
    return PROVINCES;
  }

  const country = findCountryByName(countryName);

  if (!country) {
    return [];
  }

  return State.getStatesOfCountry(country.isoCode).map((state) => ({
    name: state.name,
    code: state.isoCode,
    countryCode: state.countryCode,
  }));
};

export const findRegionByName = (countryName, regionName) => {
  const normalizedRegion = normalizeName(regionName);

  if (!countryName || !regionName) {
    return null;
  }

  if (isPhilippines(countryName) && USE_LEGACY_PHILIPPINE_LOCATIONS) {
    return PROVINCES.find(({ name }) => normalizeName(name) === normalizedRegion);
  }

  return getRegionOptions(countryName).find(
    ({ name }) => normalizeName(name) === normalizedRegion
  );
};

export const getCityOptions = (countryName, regionName) => {
  if (!countryName) {
    return [];
  }

  if (isPhilippines(countryName) && USE_LEGACY_PHILIPPINE_LOCATIONS) {
    const province = findRegionByName(countryName, regionName);

    if (!province) {
      return [];
    }

    return CITIES.filter((city) => city.province === province.key);
  }

  const country = findCountryByName(countryName);

  if (!country) {
    return [];
  }

  const region = findRegionByName(countryName, regionName);

  if (region) {
    return City.getCitiesOfState(country.isoCode, region.code).map((city) => ({
      name: city.name,
      countryCode: city.countryCode,
      stateCode: city.stateCode,
    }));
  }

  if (getRegionOptions(countryName).length === 0) {
    return City.getCitiesOfCountry(country.isoCode).map((city) => ({
      name: city.name,
      countryCode: city.countryCode,
      stateCode: city.stateCode,
    }));
  }

  return [];
};
